import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Zap, Flame, Calculator, FolderOpen, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// LUCE e GAS → Scadenze (backend: /luce-gas/scadenze in routes/pratiche_luce_gas.py). Solo le
// pratiche ATTIVE: condizioni economiche scadute o in scadenza, da richiamare con una simulazione.

const FINESTRE = [[30, '30 GIORNI'], [60, '60 GIORNI'], [90, '90 GIORNI'], [180, '6 MESI']];
const dataIt = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '');

export default function ScadenzeLuceGas() {
  const navigate = useNavigate();
  const [giorni, setGiorni] = useState(90);
  const [gestite, setGestite] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/luce-gas/scadenze`, { params: { giorni, gestite } });
      setItems(r.data || []);
    } catch (e) {
      toast.error('Errore nel caricamento delle scadenze');
    } finally {
      setLoading(false);
    }
  }, [giorni, gestite]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const segnaGestita = async (p, valore) => {
    try {
      await axios.put(`${API}/luce-gas/pratiche/${p.id}`, { scadenza_gestita: valore });
      fetchItems();
      window.dispatchEvent(new Event('jvservice:scadenze-luce-gas'));
    } catch (_) {
      toast.error('Errore');
    }
  };

  const scadute = items.filter((p) => p.giorni_alla_scadenza < 0).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scadenze-luce-gas-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Scadenze luce e gas</h1>
        <p className="text-slate-600 mt-1">Condizioni economiche in scadenza dei clienti attivati dallo studio (pratiche attive)</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-500">Scadute e in scadenza entro:</span>
            {FINESTRE.map(([g, l]) => (
              <Button key={g} type="button" size="sm" variant={giorni === g ? 'default' : 'outline'} onClick={() => setGiorni(g)}>{l}</Button>
            ))}
            <label className="flex items-center gap-1 text-sm text-slate-600 ml-2">
              <input type="checkbox" checked={gestite} onChange={(e) => setGestite(e.target.checked)} />mostra anche quelle già gestite
            </label>
            {scadute > 0 && <Badge className="bg-red-100 text-red-700 ml-auto">{scadute} già scadute</Badge>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2">Scadenza</th><th className="text-left p-2">Cliente</th><th className="text-left p-2">POD / PDR</th>
                  <th className="text-left p-2">Fornitore e offerta</th><th className="text-left p-2">Attivata il</th><th className="text-right p-2" />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-slate-500">Nessuna scadenza: compaiono qui le pratiche ATTIVE con la data di scadenza delle condizioni</td></tr>
                )}
                {items.map((p) => {
                  const g = p.giorni_alla_scadenza;
                  const colore = g < 0 ? 'bg-red-100 text-red-700' : g <= 30 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700';
                  return (
                    <tr key={p.id} className={`border-b border-slate-800 last:border-0 ${p.scadenza_gestita ? 'opacity-50' : ''}`}>
                      <td className="p-2 whitespace-nowrap">
                        <div className="font-medium">{dataIt(p.data_scadenza)}</div>
                        <Badge className={colore}>{g < 0 ? `scaduta da ${-g} gg` : g === 0 ? 'scade oggi' : `tra ${g} gg`}</Badge>
                      </td>
                      <td className="p-2 font-medium whitespace-nowrap">{p.cliente_nome}</td>
                      <td className="p-2 whitespace-nowrap">
                        {p.tipo_utenza === 'gas' ? <Flame className="w-4 h-4 text-orange-500 inline mr-1" /> : <Zap className="w-4 h-4 text-yellow-500 inline mr-1" />}
                        {p.pod_pdr}
                      </td>
                      <td className="p-2 text-xs"><div className="font-medium">{p.fornitore_a}</div><div className="text-slate-500">{p.offerta_a}</div></td>
                      <td className="p-2 whitespace-nowrap">{dataIt(p.data_attivazione)}</td>
                      <td className="p-2">
                        <div className="flex justify-end gap-1">
                          <Button type="button" size="sm" className="bg-amber-600 hover:bg-amber-700" disabled={!p.ultima_bolletta_id}
                            title={p.ultima_bolletta_id ? 'Nuova simulazione con l\'ultima bolletta' : 'Nessuna bolletta di questo POD/PDR in Fatture estrazione'}
                            onClick={() => navigate(`/luce-gas/simulazione?bolletta=${p.ultima_bolletta_id}`)}>
                            <Calculator className="w-4 h-4 mr-1" />Simula
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => navigate(`/luce-gas/pratiche?apri=${p.id}`)}>
                            <FolderOpen className="w-4 h-4 mr-1" />Pratica
                          </Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => segnaGestita(p, !p.scadenza_gestita)}
                            title="Cliente già richiamato: togli dall'elenco finché la scadenza non cambia">
                            <CheckCircle className="w-4 h-4 mr-1" />{p.scadenza_gestita ? 'Da gestire' : 'Gestita'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
